import { getDay, getMonth, getYear, isBefore } from 'date-fns'
import { AnyAction } from 'redux'

import { initialState } from '../../../store/funnelOrder/themeCities/reducer'
import { IncludeCitiesOptions } from '../../../store/funnelOrder/themeCities/types'
import { RequestResponseData } from '../../../store/types'
import { EntryKey } from '../../entries/types'
import { FORM_CHANGE } from '../../formActionTypes'
import { Giftcard } from '../giftcard'
import { SET_COUNTRY_CODE, SiteCountry } from '../locale'
import { initialTotmOrder, TotmOrder } from '../totm/surfNBeach/totmOrder'
import { CountryAirports } from './airport'
import { City } from './cities'
import { CrossSellPrices } from './crossSell'
import {
  cantPickNumDays,
  driveOptions,
  flyOptions,
  inControlOptions,
  multiDriveOptions,
  multiFlyOptions,
} from './days'
import { destinationEnum } from './destination'
import { IDiscountCode } from './discountCode'
import { driveDestinations } from './driveDestination'
import { filterDestination, getMaxExcludedCities } from './excludedCities'
import { Note } from './note'
import { roomType } from './roomType'
import { flyThemes, Theme } from './theme'
import { IncomingTime, OutgoingTime } from './times'
import {
  Baby,
  initialAdult,
  initialChild,
  initialTraveler,
  Traveler,
  TravelerType,
} from './travelers'

export interface FunnelOrder {
  themeCities: RequestResponseData<IncludeCitiesOptions[]>
  theme: Theme
  entry: EntryKey
  destination: destinationEnum
  excludedCities: string[]
  excludedCitiesIds: City[]
  includedCitiesIds: City[]
  driveDestination: driveDestinations
  days: number
  nights: number
  numberOfAdults: number
  numberOfChildren: number
  numberOfBabies: number
  numberOfPets: number
  includeBreakfast: boolean
  trainTicket: number
  trainTicket12: number
  numberOfBags: number
  extraLuggage: boolean
  travelInsurance: boolean
  cancellationInsurance: boolean
  privateRoom: boolean
  arrangeTransfer: boolean
  babyCrib: boolean
  co2Compensation: boolean
  departureDate: Date | null
  year: number | null
  month: number | null
  airports: CountryAirports[]
  outgoingTime: OutgoingTime
  incomingTime: IncomingTime
  roomType: roomType
  locations: number
  pp: number
  travelers: Traveler[]
  babies: Baby[]
  note: Note
  giftcards: Giftcard[]
  useWallet: boolean
  total: number
  wallet: number
  originalTotal: number | null

  paymentToken: string
  paymentAmount: number
  crossSellPrices: CrossSellPrices

  discountCode: IDiscountCode | null
  paymentMessage?: string
  totmOrder: TotmOrder
  flexibleRebooking: boolean
  bedAndBreakfast: boolean
}

export interface AllowedFunnelOrderValues {
  destination: destinationEnum[]
  driveDestination: driveDestinations[]
  day: number[]
  night: number[]
  outgoingTime: OutgoingTime[]
  incomingTime: IncomingTime[]
  roomType: roomType[]
  crossSellPrice: CrossSellPrices[]
}

export const initialOrder: FunnelOrder = {
  themeCities: initialState,
  destination: destinationEnum.allOfEurope,
  excludedCities: [],
  excludedCitiesIds: [],
  includedCitiesIds: [],
  driveDestination: driveDestinations.surprise,
  departureDate: null,
  year: null,
  month: null,
  theme: Theme.fly,
  entry: EntryKey.none,
  days: 3,
  nights: 2,
  locations: 1,
  numberOfAdults: 2,
  numberOfChildren: 0,
  numberOfBabies: 0,
  numberOfPets: 0,
  airports: [],
  outgoingTime: OutgoingTime.Morning,
  incomingTime: IncomingTime.Afternoon,
  roomType: roomType.basic,
  pp: 100,

  // extras
  includeBreakfast: false,
  trainTicket: 0,
  trainTicket12: 0,
  extraLuggage: false,
  numberOfBags: 0,
  travelInsurance: false,
  cancellationInsurance: false,
  privateRoom: false,
  arrangeTransfer: false,
  babyCrib: false,
  co2Compensation: false,
  flexibleRebooking: false,
  bedAndBreakfast: false,

  // travelers
  travelers: [
    initialTraveler,
    { ...initialTraveler, Id: 1, MainTraveler: false },
  ],
  babies: [],

  note: {
    UseNote: true,
    NoteText: '',
  },
  giftcards: [],

  // delete when implemented
  total: NaN,
  useWallet: false,
  wallet: 0,
  originalTotal: null,

  paymentToken: '',
  paymentAmount: NaN,
  crossSellPrices: {
    cancellationInsurance: NaN,
    extraLuggage: NaN,
    travelInsurance: NaN,
    includeBreakfast: NaN,
    trainTicket: NaN,
    trainTicket12: NaN,
    privateRoom: NaN,
    pets: NaN,
    babyCribs: NaN,
    passProtection: NaN,
    surfEasy: NaN,
    surfHard: NaN,
    co2Compensation: NaN,
    flexibleRebooking: NaN,
    bedAndBreakfast: NaN,
  },

  // totm
  discountCode: null,
  totmOrder: initialTotmOrder,
}

export default function funnelOrderReducer(
  currentState: FunnelOrder = initialOrder,
  action: AnyAction,
): FunnelOrder {
  switch (action.type) {
    case SET_COUNTRY_CODE:
      const newState = Object.assign({}, currentState)
      // set country for each traveller
      const newCountry = action.country
      const newTravelers = []
      for (let i = 0; i < newState.travelers.length; i++) {
        newTravelers.push({
          ...newState.travelers[i],
          Country: newCountry,
        })
      }
      newState.travelers = newTravelers

      // extras
      if (newCountry === SiteCountry.uk || newCountry === SiteCountry.ie) {
        newState.co2Compensation = false
      }

      // reset total of order
      newState.total = NaN

      // check if theme is still valid, if not, change to "fly'
      if (
        currentState.theme === Theme.drive &&
        ![SiteCountry.nl, SiteCountry.be].includes(action.country)
      ) {
        newState.theme = Theme.fly
      }

      // reset to cheapest in the current month if in entry
      if (currentState.entry !== EntryKey.none) {
        newState.departureDate = null
      }

      return newState
    case FORM_CHANGE:
      const changedState = { ...currentState }

      // limit the allowed number of pets
      if (
        [
          'funnelOrder.numberOfAdults',
          'funnelOrder.numberOfChildren',
          'funnelOrder',
        ].includes(action.model)
      ) {
        const numberOfPeople =
          currentState.numberOfAdults + currentState.numberOfChildren
        const maxNumberOfPets = Math.ceil(numberOfPeople / 2) * 2
        if (currentState.numberOfPets > maxNumberOfPets) {
          changedState.numberOfPets = maxNumberOfPets
        }
      }

      if (action.model === 'funnelOrder.destination') {
        changedState.excludedCitiesIds = filterDestination(
          changedState.excludedCitiesIds,
          changedState.theme,
          changedState.destination,
        )
        changedState.excludedCitiesIds = changedState.excludedCitiesIds.slice(
          0,
          getMaxExcludedCities(changedState.theme, changedState.destination),
        )
      }

      // reset extras if the theme, destination or room type change
      if (
        action.model === 'funnelOrder.theme' ||
        action.model === 'funnelOrder.entry' ||
        action.model === 'funnelOrder.destination' ||
        action.model === 'funnelOrder.roomType'
      ) {
        changedState.numberOfBags = 0
        changedState.trainTicket = 0
        changedState.trainTicket12 = 0

        const extraKeys = [
          'includeBreakfast',
          'extraLuggage',
          'travelInsurance',
          'cancellationInsurance',
          'privateRoom',
          'arrangeTransfer',
          'babyCrib',
          'co2Compensation',
        ]

        extraKeys.forEach(key => {
          ;(changedState as any)[key] = false
        })
      }

      if (action.model === 'funnelOrder.roomType') {
        changedState.bedAndBreakfast = false
      }

      if (action.model === 'funnelOrder.bedAndBreakfast' && action.value) {
        changedState.includeBreakfast = false
      }

      // reset price when options change
      const keys = [
        'destination',
        'theme',
        'days',
        'numberOfAdults',
        'numberOfChildren',
        'numberOfBabies',
        'roomType',
        'locations',
        'airports',
        'entry',
      ]
      if (keys.map(key => `funnelOrder.${key}`).includes(action.model)) {
        changedState.total = NaN

        if (
          flyThemes.includes(changedState.theme) &&
          changedState.roomType === roomType.budget
        ) {
          changedState.privateRoom =
            changedState.numberOfChildren + changedState.numberOfBabies > 0
        }
      }

      if (
        action.model === 'funnelOrder.entry' &&
        changedState.entry === EntryKey.none
      ) {
        changedState.numberOfAdults = 2
        changedState.excludedCitiesIds = []
      }
      // correct destinations/number of days when theme changes
      if (
        action.model === 'funnelOrder.theme' ||
        action.model === 'funnelOrder'
      ) {
        if ([Theme.fly, Theme.train].includes(changedState.theme)) {
          changedState.locations = 1
          // Can't have certain days
          if (!flyOptions.map(x => x.days).includes(changedState.days)) {
            changedState.days = 3
          }

          // Can't have 7 days trip inside europe
          if (
            changedState.days === 7 &&
            changedState.destination !== destinationEnum.outsideEurope
          ) {
            changedState.destination = destinationEnum.outsideEurope
          }

          // Can only have 7 days trip outside europe
          if (
            changedState.days !== 7 &&
            changedState.destination === destinationEnum.outsideEurope
          ) {
            changedState.destination = destinationEnum.allOfEurope
          }
        } else if (changedState.theme === Theme.drive) {
          changedState.locations = 1
          // Can't have certain days and change lowest days
          if (changedState.days === 3) {
            changedState.days = 2
          } else if (
            !driveOptions.map(x => x.days).includes(changedState.days)
          ) {
            changedState.days = 4
          }

          // Default of comfort for drive
          changedState.roomType = roomType.basic
        } else if (changedState.theme === Theme.multiFly) {
          // Gotta make it multi locational
          if (
            !multiFlyOptions
              .map(x => x.locations)
              .includes(changedState.locations)
          ) {
            changedState.locations = 2
          }

          // Need to check the days are correct
          if (
            cantPickNumDays(
              multiFlyOptions,
              changedState.days,
              changedState.locations,
            )
          ) {
            changedState.days = multiFlyOptions.filter(
              x => x.locations === changedState.locations,
            )[0].days[0].days
          }
          changedState.destination = destinationEnum.allOfEurope

          // Need to check timings
          if (changedState.outgoingTime === OutgoingTime.WholeDay) {
            changedState.outgoingTime = OutgoingTime.Morning
          }
          if (changedState.incomingTime === IncomingTime.WholeDay) {
            changedState.incomingTime = IncomingTime.Afternoon
          }
        } else if (changedState.theme === Theme.multiDrive) {
          // Got to make sure it's multi locational
          if (
            !multiDriveOptions
              .map(x => x.locations)
              .includes(changedState.locations)
          ) {
            changedState.locations = 2
          }

          // Need to check the days are correct
          if (
            cantPickNumDays(
              multiDriveOptions,
              changedState.days,
              changedState.locations,
            )
          ) {
            changedState.days = multiDriveOptions.filter(
              x => x.locations === changedState.locations,
            )[0].days[0].days
          }

          // Default of comfort for drive
          changedState.roomType = roomType.basic
        } else if (changedState.theme === Theme.totm) {
          // Can only have 5 or 7 days trip
          if (![5, 7].includes(changedState.days)) {
            changedState.days = 5
          }

          if (changedState.roomType !== roomType.budget) {
            changedState.roomType = roomType.budget
          }
        } else if (changedState.theme === Theme.solo) {
          changedState.numberOfAdults = 1
          changedState.numberOfChildren = 0
          changedState.numberOfBabies = 0
          changedState.numberOfPets = 0
          changedState.babies = []
          changedState.destination = destinationEnum.allOfEurope
          changedState.roomType = roomType.budget
          changedState.days = 4
          changedState.privateRoom = false
        } else if (changedState.theme === Theme.incontrol) {
          changedState.locations = 1
          // Can't have certain days
          if (!inControlOptions.map(x => x.days).includes(changedState.days)) {
            changedState.days = 3
          }
          changedState.destination = destinationEnum.allOfEurope
        }
        changedState.excludedCities = []
        changedState.excludedCitiesIds = []
        changedState.includedCitiesIds = []
      }

      // if number of people changed
      if (
        [
          'funnelOrder.numberOfAdults',
          'funnelOrder.numberOfChildren',
          'funnelOrder.numberOfBabies',
        ].includes(action.model) ||
        changedState.numberOfAdults !== currentState.numberOfAdults ||
        changedState.numberOfChildren !== currentState.numberOfChildren ||
        changedState.numberOfBabies !== currentState.numberOfBabies
      ) {
        let travelers = changedState.travelers.filter(x => x !== null)
        let currentId = travelers[travelers.length - 1]
          ? travelers[travelers.length - 1].Id
          : 0

        // Adults
        const adults = travelers.filter(x => x.Type === TravelerType.Adult)
        const mainBooker = adults.length > 0 ? adults[0] : initialAdult
        const adultDiff = changedState.numberOfAdults - adults.length
        //
        if (adultDiff > 0) {
          for (let i = 0; i < adultDiff; i++) {
            if (adults.length === 0) {
              adults.push(Object.assign({}, initialTraveler))
            } else {
              currentId++
              adults.push(
                Object.assign({}, initialAdult, {
                  Id: currentId,
                  Country: mainBooker.Country,
                }),
              )
            }
          }
        } else if (adultDiff < 0) {
          for (let i = 0; i < -adultDiff; i++) {
            adults.pop()
          }
        }

        // Children
        const children = travelers.filter(x => x.Type === TravelerType.Child)
        const childDiff = changedState.numberOfChildren - children.length
        if (childDiff > 0) {
          for (let i = 0; i < childDiff; i++) {
            currentId++
            children.push(
              Object.assign({}, initialChild, {
                Id: currentId,
                Country: mainBooker.Country,
              }),
            )
          }
        } else if (childDiff < 0) {
          for (let i = 0; i < -childDiff; i++) {
            children.pop()
          }
        }

        // Combine
        travelers = adults.concat(children)
        changedState.travelers = travelers

        // Babies
        const babies: Baby[] = []
        for (let i = 0; i < changedState.numberOfBabies; i++) {
          babies.push({ Id: i, Gender: 'Male' })
        }
        changedState.babies = babies
      }

      if (currentState.departureDate) {
        const departureDate = new Date(currentState.departureDate)
        const now = new Date()
        const today = new Date(getYear(now), getMonth(now), getDay(now))
        if (isBefore(departureDate, today)) {
          changedState.departureDate = today
        }
      }

      return changedState
    default:
      return currentState
  }
}
